/**
 * Session Init API
 *
 * Initializes a session with the backend and retrieves a JWT token.
 *
 * Two modes:
 * - No sessionId: backend creates a new session and returns sessionId + JWT
 * - With sessionId: backend returns the same sessionId + fresh JWT token
 */
import { SESSION_ENDPOINTS } from './endpoints';
import { buildHeaders } from './utils';
import type { SessionInitRequest, SessionInitResponse } from '@contracts';
import { logger } from '@/shared/utils/logger';

export class SessionInitError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly errorCode?: string,
  ) {
    super(message);
    this.name = 'SessionInitError';
  }
}

/**
 * In-flight init request for deduplication.
 * When multiple callers trigger init with the same sessionId concurrently
 * (e.g. error recovery from parallel API calls), only one HTTP request is made.
 */
let inflightInit: {
  sessionId: string | undefined;
  promise: Promise<SessionInitResponse>;
} | null = null;

/**
 * Call POST /api/session/init to initialize a session and get a JWT token.
 *
 * Deduplicates concurrent calls with the same sessionId — if an identical
 * init request is already in-flight, callers share its promise instead of
 * firing a second HTTP request.
 *
 * @param sessionId - Optional. If provided, re-initializes that session's token.
 *                    If omitted, backend creates a new session.
 * @returns Session init response with sessionId, token, and expiresAt
 * @throws SessionInitError on failure
 */
export async function initSessionApi(sessionId?: string): Promise<SessionInitResponse> {
  if (inflightInit && inflightInit.sessionId === sessionId) {
    return inflightInit.promise;
  }

  const promise = executeInitSessionApi(sessionId);
  inflightInit = { sessionId, promise };

  try {
    return await promise;
  } finally {
    if (inflightInit?.promise === promise) {
      inflightInit = null;
    }
  }
}

async function executeInitSessionApi(sessionId?: string): Promise<SessionInitResponse> {
  const body: SessionInitRequest = sessionId ? { sessionId } : {};

  const response = await fetch(SESSION_ENDPOINTS.INIT, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });

  const data: unknown = await response.json();

  if (!response.ok) {
    const errorDetails = (data as { errorDetails?: { errorMessage?: string; errorCode?: string } })
      ?.errorDetails;

    const errorMessage = errorDetails?.errorMessage ?? 'Session init failed';
    const errorCode = errorDetails?.errorCode;

    logger.error('[SessionInit] Failed:', { statusCode: response.status, errorMessage, errorCode });
    throw new SessionInitError(errorMessage, response.status, errorCode);
  }

  const initResponse = data as SessionInitResponse;
  logger.info('[SessionInit] Session initialized:', initResponse.sessionId);
  return initResponse;
}
